import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { BarChart3, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

const eur = (v) => `${Math.round(v).toLocaleString('fr-FR')} €`;

const SCOPES = [
  { id: 'confirmed', label: 'Confirmées' },
  { id: 'all', label: 'Toutes' },
  { id: 'unconfirmed', label: 'Non confirmées' },
];

const PLAN_OPTIONS = [
  { id: 'all', label: 'Toutes formules' },
  { id: 'essentielle', label: 'Essentielle' },
  { id: 'evolution', label: 'Évolution' },
  { id: 'transformation', label: 'Transformation' },
  { id: 'single', label: "Cours à l'unité" },
];

const PRICING_OPTIONS = [
  { id: 'all', label: 'Tous tarifs' },
  { id: 'privilege', label: 'Pré-lancement −20%' },
  { id: 'normal', label: 'Tarif normal' },
];

const selectCls = "h-9 rounded-lg border border-[#E9DDCC] bg-white px-2 text-xs text-[#493329] focus:outline-none focus:ring-2 focus:ring-[#C99A55]";

export default function StatsProPanel({ onDrilldown }) {
  const drill = (seg) => { if (onDrilldown) onDrilldown(seg); };
  const [data, setData] = useState(null);
  const [scope, setScope] = useState('confirmed');
  const [filters, setFilters] = useState({ status: 'confirmed', plan: 'all', pricing: 'all' });
  const [segment, setSegment] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/stats/full`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error('Impossible de charger les statistiques détaillées.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const run = async () => {
      try {
        const res = await axios.get(`${API}/admin/stats/segment`, { params: filters, withCredentials: true });
        setSegment(res.data);
      } catch (e) { setSegment(null); }
    };
    run();
  }, [filters]);

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats/export-clients-csv`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PURO_clientes_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé.');
    } catch (e) {
      toast.error('Export CSV impossible.');
    }
  };

  if (loading || !data) {
    return (
      <Card className="border-[#E9DDCC] rounded-2xl">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[#C99A55]" />
        </CardContent>
      </Card>
    );
  }

  const sc = data.scopes[scope];
  const priv = sc.privilege;
  const norm = sc.normal;
  const cap = data.capacity;
  const plans = Object.entries(sc.plans);
  const scopeLabel = SCOPES.find((s) => s.id === scope)?.label || '';

  return (
    <Card className="border border-[#E9DDCC] rounded-2xl bg-white" data-testid="stats-pro-panel">
      <CardContent className="p-6 space-y-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className="w-4 h-4 text-[#C99A55]" strokeWidth={1.8} />
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#C99A55] font-semibold">Statistiques détaillées</span>
            </div>
            <h3 className="font-heading text-lg text-[#493329]">Segmentation tarifaire & formules</h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={downloadCsv}
              className="px-3 py-1.5 rounded-full text-xs border border-[#C99A55]/50 text-[#9A744C] hover:bg-[#C99A55] hover:text-white transition-colors"
              data-testid="export-csv-btn">
              Exporter CSV (Excel)
            </button>
            <div className="flex gap-1 bg-[#F5EFE5] rounded-full p-1 border border-[#E9DDCC]">
              {SCOPES.map((sOpt) => (
                <button key={sOpt.id} onClick={() => setScope(sOpt.id)}
                  className={`px-3 py-1.5 rounded-full text-xs transition-colors ${scope === sOpt.id ? 'bg-[#C99A55] text-white' : 'text-[#7A6455]'}`}
                  data-testid={`stats-scope-${sOpt.id}`}>
                  {sOpt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Segmentation 3 catégories — cliquables */}
        <div className="grid grid-cols-3 gap-3">
          <button onClick={() => drill({ status: 'all', label: 'Préinscriptions (toutes)' })}
            className="bg-[#F5EFE5] rounded-xl p-3 text-center border border-[#E9DDCC] hover:border-[#C99A55] hover:shadow-sm transition-all cursor-pointer"
            data-testid="drill-pre">
            <div className="text-[10px] uppercase text-[#AE9885] tracking-wider mb-1">Préinscriptions</div>
            <div className="font-heading text-2xl text-[#493329]" data-testid="stats-total-pre">{data.scopes.all.count}</div>
            <div className="text-[9px] text-[#C99A55] mt-0.5">Voir la liste →</div>
          </button>
          <button onClick={() => drill({ status: 'confirmed', label: 'Confirmées' })}
            className="bg-[#F5EFE5] rounded-xl p-3 text-center border border-[#C99A55]/40 hover:border-[#C99A55] hover:shadow-sm transition-all cursor-pointer"
            data-testid="drill-confirmed">
            <div className="text-[10px] uppercase text-[#C99A55] tracking-wider mb-1">Confirmées</div>
            <div className="font-heading text-2xl text-[#C99A55]" data-testid="stats-confirmed">{data.scopes.confirmed.count}</div>
            <div className="text-[9px] text-[#C99A55] mt-0.5">Voir la liste →</div>
          </button>
          <button onClick={() => drill({ status: 'unconfirmed', label: 'Non confirmées' })}
            className="bg-[#F5EFE5] rounded-xl p-3 text-center border border-[#E9DDCC] hover:border-[#C99A55] hover:shadow-sm transition-all cursor-pointer"
            data-testid="drill-unconfirmed">
            <div className="text-[10px] uppercase text-[#AE9885] tracking-wider mb-1">Non confirmées</div>
            <div className="font-heading text-2xl text-[#493329]" data-testid="stats-unconfirmed">{data.scopes.unconfirmed.count}</div>
            <div className="text-[9px] text-[#C99A55] mt-0.5">Voir la liste →</div>
          </button>
        </div>

        {data.scopes.confirmed.no_plan > 0 && (
          <button onClick={() => drill({ status: 'confirmed', plan: 'none', label: 'Confirmées sans formule choisie' })}
            className="w-full flex items-center justify-between bg-[#FFF8E6] border border-[#C99A55]/40 rounded-xl px-4 py-2.5 text-sm hover:border-[#C99A55] transition-colors cursor-pointer" data-testid="stats-noplan-line">
            <span className="text-[#493329]">Confirmées <strong>sans formule choisie</strong> — voir la liste →</span>
            <strong className="text-[#9A744C]">{data.scopes.confirmed.no_plan} ({data.scopes.confirmed.no_plan_percent} %)</strong>
          </button>
        )}

        {data.consistency && !data.consistency.all_ok && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-800" data-testid="stats-consistency-alert">
            ⚠ Incohérence détectée dans les calculs : {data.consistency.checks.filter((c) => !c.ok).map((c) => c.label).join(' ; ')}
          </div>
        )}

        {/* -20% vs plein tarif — cliquables */}
        <div className="grid md:grid-cols-2 gap-3">
          <div onClick={() => drill({ status: scope, pricing: 'privilege', label: `Fondatrices −20% — ${scopeLabel}` })}
            className="rounded-xl border border-[#C99A55]/40 bg-[#F3EADA] p-4 space-y-1.5 hover:border-[#C99A55] hover:shadow-sm transition-all cursor-pointer" data-testid="stats-privilege-block">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-wider text-[#C99A55] font-semibold">Pré-lancement −20%</div>
              <span className="text-[9px] text-[#C99A55]">Voir la liste →</span>
            </div>
            <div className="flex justify-between text-sm"><span className="text-[#7A6455]">Clientes</span><strong>{priv.count} ({priv.percent} %)</strong></div>
            <div className="flex justify-between text-sm"><span className="text-[#7A6455]">CA mensuel réel</span><strong>{eur(priv.ca_mensuel_reel)}</strong></div>
            <div className="flex justify-between text-sm"><span className="text-[#7A6455]">CA avant remise</span><span>{eur(priv.ca_mensuel_avant_remise)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-[#7A6455]">Remises / mois</span><span className="text-[#C76542]">−{eur(priv.remise_mensuelle)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-[#7A6455]">Remises / an</span><span className="text-[#C76542]">−{eur(priv.remise_annuelle)}</span></div>
          </div>
          <div onClick={() => drill({ status: scope, pricing: 'normal', label: `Tarif normal — ${scopeLabel}` })}
            className="rounded-xl border border-[#E9DDCC] bg-white p-4 space-y-1.5 hover:border-[#C99A55] hover:shadow-sm transition-all cursor-pointer" data-testid="stats-normal-block">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-wider text-[#7A6455] font-semibold">Tarif normal</div>
              <span className="text-[9px] text-[#C99A55]">Voir la liste →</span>
            </div>
            <div className="flex justify-between text-sm"><span className="text-[#7A6455]">Clientes</span><strong>{norm.count} ({norm.percent} %)</strong></div>
            <div className="flex justify-between text-sm"><span className="text-[#7A6455]">CA mensuel</span><strong>{eur(norm.ca_mensuel)}</strong></div>
            <div className="flex justify-between text-sm"><span className="text-[#7A6455]">CA annuel récurrent projeté</span><span>{eur(norm.ca_annuel_recurrent)}</span></div>
            <div className="flex justify-between text-sm pt-1 border-t border-[#EFE4D0]"><span className="text-[#7A6455]">CA récurrent (abonnements)</span><strong>{eur(sc.ca_recurrent)}</strong></div>
            <div className="flex justify-between text-sm"><span className="text-[#7A6455]">CA non récurrent (ponctuel)</span><span>{eur(sc.ca_non_recurrent)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-[#7A6455]">CA total scope</span><strong>{eur(sc.ca_mensuel_total)}</strong></div>
          </div>
        </div>

        {/* Paniers moyens */}
        <div className="grid grid-cols-3 gap-3 text-center" data-testid="stats-paniers-block">
          <div className="bg-[#F5EFE5] rounded-xl p-3 border border-[#E9DDCC]">
            <div className="text-[10px] uppercase text-[#AE9885] tracking-wider mb-1">Panier moyen — toutes</div>
            <div className="font-heading text-xl text-[#493329]" data-testid="panier-all">{eur(sc.panier_moyen_all)}</div>
          </div>
          <div className="bg-[#F5EFE5] rounded-xl p-3 border border-[#C99A55]/40">
            <div className="text-[10px] uppercase text-[#C99A55] tracking-wider mb-1">Avec formule choisie</div>
            <div className="font-heading text-xl text-[#C99A55]" data-testid="panier-formule">{eur(sc.panier_moyen_avec_formule)}</div>
          </div>
          <div className="bg-[#F5EFE5] rounded-xl p-3 border border-[#E9DDCC]">
            <div className="text-[10px] uppercase text-[#AE9885] tracking-wider mb-1">Abonnements récurrents</div>
            <div className="font-heading text-xl text-[#493329]" data-testid="panier-recurrent">{eur(sc.panier_moyen_recurrent)}</div>
          </div>
        </div>

        {/* Ventilation par formule */}
        <div className="overflow-x-auto rounded-xl border border-[#E9DDCC]">
          <table className="w-full text-xs" data-testid="stats-plan-table">
            <thead className="bg-[#493329] text-white">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Formule</th>
                <th className="text-right px-2 py-2 font-medium">Clientes</th>
                <th className="text-right px-2 py-2 font-medium">%</th>
                <th className="text-right px-2 py-2 font-medium">−20%</th>
                <th className="text-right px-2 py-2 font-medium">Plein tarif</th>
                <th className="text-right px-2 py-2 font-medium">CA −20%</th>
                <th className="text-right px-2 py-2 font-medium">CA normal</th>
                <th className="text-right px-2 py-2 font-medium">CA total</th>
                <th className="text-right px-2 py-2 font-medium">Séances/mois</th>
                <th className="text-right px-2 py-2 font-medium">% capacité</th>
                <th className="text-right px-3 py-2 font-medium">€/séance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EFE4D0]">
              {plans.map(([pid, p]) => (
                <tr key={p.name} onClick={() => drill({ status: scope, plan: pid, label: `${p.name} — ${scopeLabel}` })}
                  className="hover:bg-[#FAF6EF] cursor-pointer" data-testid={`drill-plan-${pid}`}>
                  <td className="px-3 py-2 font-medium text-[#493329]">{p.name}<span className="block text-[10px] text-[#AE9885]">{p.tarif_normal}€ / {p.tarif_lancement}€ (−20%)</span></td>
                  <td className="px-2 py-2 text-right">{p.count}</td>
                  <td className="px-2 py-2 text-right">{p.percent_portfolio} %</td>
                  <td className="px-2 py-2 text-right">{p.n_privilege}</td>
                  <td className="px-2 py-2 text-right">{p.n_normal}</td>
                  <td className="px-2 py-2 text-right">{eur(p.ca_privilege)}</td>
                  <td className="px-2 py-2 text-right">{eur(p.ca_normal)}</td>
                  <td className="px-2 py-2 text-right font-semibold">{eur(p.ca_total)}</td>
                  <td className="px-2 py-2 text-right">{p.sessions_mensuelles}</td>
                  <td className="px-2 py-2 text-right">{p.percent_capacity} %</td>
                  <td className="px-3 py-2 text-right">{p.sessions_mensuelles ? `${p.revenue_per_session} €` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Filtres combinables */}
        <div className="rounded-xl border border-[#E9DDCC] bg-[#F5EFE5] p-4" data-testid="stats-filters-block">
          <div className="text-[10px] uppercase tracking-wider text-[#7A6455] font-semibold mb-2">Filtres combinables</div>
          <div className="flex flex-wrap gap-2 mb-3">
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={selectCls} data-testid="filter-status">
              {SCOPES.map((o) => <option key={o.id} value={o.id === 'all' ? 'all' : o.id}>{o.label}</option>)}
            </select>
            <select value={filters.plan} onChange={(e) => setFilters({ ...filters, plan: e.target.value })} className={selectCls} data-testid="filter-plan">
              {PLAN_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            <select value={filters.pricing} onChange={(e) => setFilters({ ...filters, pricing: e.target.value })} className={selectCls} data-testid="filter-pricing">
              {PRICING_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
          {segment && (
            <div className="grid grid-cols-3 gap-3 text-center" data-testid="filter-results">
              <div className="bg-white rounded-lg p-2.5 border border-[#E9DDCC]">
                <div className="text-[10px] uppercase text-[#AE9885]">Clientes</div>
                <div className="font-heading text-xl text-[#493329]" data-testid="filter-count">{segment.count}</div>
              </div>
              <div className="bg-white rounded-lg p-2.5 border border-[#E9DDCC]">
                <div className="text-[10px] uppercase text-[#AE9885]">CA mensuel</div>
                <div className="font-heading text-xl text-[#493329]" data-testid="filter-ca">{eur(segment.ca_mensuel)}</div>
              </div>
              <div className="bg-white rounded-lg p-2.5 border border-[#E9DDCC]">
                <div className="text-[10px] uppercase text-[#AE9885]">Séances/mois</div>
                <div className="font-heading text-xl text-[#493329]" data-testid="filter-sessions">{segment.sessions_mensuelles}</div>
              </div>
            </div>
          )}
        </div>

        {/* Capacité */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center" data-testid="stats-capacity-block">
          <div className="bg-[#F5EFE5] rounded-xl p-3 border border-[#E9DDCC]">
            <div className="text-[10px] uppercase text-[#AE9885] tracking-wider mb-1">Capacité réelle / mois</div>
            <div className="font-heading text-xl text-[#493329]">{cap.real.monthly_seats}</div>
          </div>
          <div className="bg-[#F5EFE5] rounded-xl p-3 border border-[#E9DDCC]">
            <div className="text-[10px] uppercase text-[#AE9885] tracking-wider mb-1">Engagées (conf.)</div>
            <div className="font-heading text-xl text-[#493329]">{cap.engaged_seats_confirmed}</div>
          </div>
          <div className="bg-[#F5EFE5] rounded-xl p-3 border border-[#E9DDCC]">
            <div className="text-[10px] uppercase text-[#AE9885] tracking-wider mb-1">Remplissage conf.</div>
            <div className="font-heading text-xl text-[#C99A55]">{cap.fill_rate_confirmed} %</div>
            {cap.saturation_confirmed && (
              <div className={`mt-1 inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${
                cap.saturation_confirmed.level === 'red' ? 'bg-red-100 text-red-700'
                  : cap.saturation_confirmed.level === 'orange' ? 'bg-amber-100 text-amber-700'
                  : 'bg-emerald-100 text-emerald-700'}`}
                data-testid="saturation-badge">
                {cap.saturation_confirmed.label}
              </div>
            )}
          </div>
          <div className="bg-[#F5EFE5] rounded-xl p-3 border border-[#E9DDCC]">
            <div className="text-[10px] uppercase text-[#AE9885] tracking-wider mb-1">Remplissage total</div>
            <div className="font-heading text-xl text-[#493329]">{cap.fill_rate_all} %</div>
          </div>
        </div>

        <p className="text-[11px] text-[#AE9885] italic">
          Capacité réelle : {cap.monthly_hours} h de salle louées/mois × {cap.reformers} Reformer = {cap.real.monthly_seats} places.
          Capacité théorique du planning (indicative) : {cap.theoretical.monthly_seats} places. Les taux affichés utilisent la capacité réelle — paramètres modifiables dans l'onglet « Studio » → « Paramètres du studio ».
        </p>
      </CardContent>
    </Card>
  );
}
